import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {App, Form} from "antd";
import {useTranslation} from "react-i18next";
import {AuthContext} from "../../../Contexts/AuthContext";
import {
    useChangeEventRegistrationPayerMutation,
    useDeleteEventParticipantMutation,
    useEventParticipantActivityOptionsQuery,
    useEventParticipantDetailFallbackQuery,
    useEventParticipantHotelOptionsQuery,
    useEventParticipantOperationsDetailQuery,
    useEventParticipantRoomAllotmentOptionsQuery,
    useEventParticipantRoomTypeOptionsQuery,
    useGenerateEventCertificateMutation,
    useInviteEventRegistrationMutation,
    useParticipantPayerOptionsQuery,
    useRejectEventRegistrationMutation,
    useResendEventCertificateEmailMutation,
    useResendEventRegistrationVerificationEmailMutation,
    useSaveEventRoomReservationMutation,
    useUpdateEventParticipantMutation,
    useUpdateEventRegistrationActivitiesMutation
} from "../../../GraphQL/generated/graphql";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../../userFacingErrorUtils";
import {authorizationHeaders} from "../../../api/authorization";
import {downloadResponseFile} from "../../../downloadFile";
import {normalizeEnumKey} from "../../../i18n/labelHelpers";
import {formatLocalDate} from "../adminDateFormat";
import {
    buildParticipantOperationsFileDownloadUrl,
    buildParticipantOperationsVariables,
    canGenerateCertificateForRegistration,
    EMPTY_OPERATIONS_VALUE
} from "../participantOperationsHelpers";

import {
    buildActivityAnswerInputs,
    buildActivityFormValues,
    buildParticipantFullName,
    toPayerTypeInput,
    isLikelyValidEmail,
    isQrInvitable,
    isRegistrationRejectable,
    normalizeEventActivityQuestionType,
    payerFormValuesFromPayer,
    pickPrimaryRegistration,
    resolveVisibleActivitySelection,
    toPositiveIds,
    toPositiveInt
} from "./participantDetailHelpers";
import type {
    ActivityFormValues,
    EventActivityOption,
    EventParticipantHotelOption,
    EventParticipantRoomAllotmentOption,
    EventParticipantRoomTypeOption,
    ParticipantCertificate,
    ParticipantDetailTarget,
    ParticipantFormValues,
    ParticipantPayerOption,
    ParticipantRegistration,
    ParticipantRegistrationCode,
    ParticipantRoomReservation,
    PayerFormValues,
    QrInvitationState,
    RoomReservationFormValues,
    RoomReservationOption
} from "./participantDetailTypes";

const presentItems = <T, >(items?: Array<T | null> | null): T[] =>
    (items ?? []).filter((item): item is T => !!item);

const positiveIdsOrPlaceholder = (ids: number[]) => {
    const validIds = ids.filter(id => Number.isFinite(id) && id > 0);

    return validIds.length ? validIds : [-1];
};

// State, data and actions for the participant detail page. Sections read from the
// returned model so every mutation keeps one refresh path.
export const useParticipantDetail = (target: ParticipantDetailTarget, onDeleted?: () => void) => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const errorLabels = buildUserFacingErrorLabels(t);
    const {auth} = useContext(AuthContext);
    const [participantForm] = Form.useForm<ParticipantFormValues>();
    const [payerForm] = Form.useForm<PayerFormValues>();
    const [roomReservationForm] = Form.useForm<RoomReservationFormValues>();
    const [activityForm] = Form.useForm<ActivityFormValues>();
    const [rejectForm] = Form.useForm<{reason: string}>();
    const [downloadingCertificateId, setDownloadingCertificateId] = useState<number>();
    const [generatingCertificateRegistrationId, setGeneratingCertificateRegistrationId] = useState<number>();
    const [resendingCertificateId, setResendingCertificateId] = useState<number>();
    const [invitingQrRegistrationId, setInvitingQrRegistrationId] = useState<number>();
    const [qrModalCodeId, setQrModalCodeId] = useState<number>();
    const [resendingVerificationRegistrationId, setResendingVerificationRegistrationId] = useState<number>();
    const [rejectingRegistration, setRejectingRegistration] = useState<ParticipantRegistration>();
    const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
    const [savingParticipant, setSavingParticipant] = useState(false);
    const [savingRegistration, setSavingRegistration] = useState(false);
    const [changingPayer, setChangingPayer] = useState(false);
    const [savingActivities, setSavingActivities] = useState(false);
    const [savingRoomReservation, setSavingRoomReservation] = useState(false);
    const [payerSearch, setPayerSearch] = useState("");
    const roomReservationHydrationKeyRef = useRef<string>();

    const detailVariables = useMemo(
        () => buildParticipantOperationsVariables(target.eventId, target.participantId),
        [target.eventId, target.participantId]
    );
    const detailQuery = useEventParticipantOperationsDetailQuery({
        variables: detailVariables,
        skip: !detailVariables,
        fetchPolicy: "cache-and-network"
    });
    const fallbackParticipantQuery = useEventParticipantDetailFallbackQuery({
        variables: {id: target.participantId},
        skip: !target.participantId,
        fetchPolicy: "cache-and-network"
    });
    const payerOptionsQuery = useParticipantPayerOptionsQuery({
        variables: {search: payerSearch.trim()},
        fetchPolicy: "cache-and-network"
    });
    const hotelsQuery = useEventParticipantHotelOptionsQuery({
        variables: {eventId: target.eventId},
        skip: !target.eventId,
        fetchPolicy: "cache-and-network"
    });
    const hotelOptions = useMemo(
        () => presentItems<EventParticipantHotelOption>(hotelsQuery.data?.eventHotels?.items),
        [hotelsQuery.data?.eventHotels?.items]
    );
    const hotelIds = useMemo(() => positiveIdsOrPlaceholder(hotelOptions.map(hotel => hotel.id)), [hotelOptions]);
    const roomTypesQuery = useEventParticipantRoomTypeOptionsQuery({
        variables: {hotelIds},
        skip: !target.eventId,
        fetchPolicy: "cache-and-network"
    });
    const roomTypeOptions = useMemo(
        () => presentItems<EventParticipantRoomTypeOption>(roomTypesQuery.data?.eventRoomTypes?.items),
        [roomTypesQuery.data?.eventRoomTypes?.items]
    );
    const roomTypeIds = useMemo(
        () => positiveIdsOrPlaceholder(roomTypeOptions.map(roomType => roomType.id)),
        [roomTypeOptions]
    );
    const roomAllotmentsQuery = useEventParticipantRoomAllotmentOptionsQuery({
        variables: {hotelIds, roomTypeIds},
        skip: !target.eventId,
        fetchPolicy: "cache-and-network"
    });
    const accommodationOptionsLoading = hotelsQuery.loading || roomTypesQuery.loading || roomAllotmentsQuery.loading;
    const accommodationOptionsFailed = !!(hotelsQuery.error || roomTypesQuery.error || roomAllotmentsQuery.error
        || hotelsQuery.data?.eventHotels === null
        || roomTypesQuery.data?.eventRoomTypes === null
        || roomAllotmentsQuery.data?.eventRoomAllotments === null);
    const activityOptionsQuery = useEventParticipantActivityOptionsQuery({
        variables: {eventId: target.eventId},
        skip: !target.eventId,
        fetchPolicy: "cache-and-network"
    });
    const [generateCertificate] = useGenerateEventCertificateMutation();
    const [inviteRegistration] = useInviteEventRegistrationMutation();
    const [resendRegistrationVerification] = useResendEventRegistrationVerificationEmailMutation();
    const [resendCertificateEmail] = useResendEventCertificateEmailMutation();
    const [deleteEventParticipant, deleteParticipantState] = useDeleteEventParticipantMutation();
    const [updateEventParticipant] = useUpdateEventParticipantMutation();
    const [rejectEventRegistration] = useRejectEventRegistrationMutation();
    const [changeEventRegistrationPayer] = useChangeEventRegistrationPayerMutation();
    const [updateEventRegistrationActivities] = useUpdateEventRegistrationActivitiesMutation();
    const [saveEventRoomReservation] = useSaveEventRoomReservationMutation();

    const refetchDetail = detailQuery.refetch;
    const detail = detailQuery.data?.eventParticipantOperationsDetail;
    const fallbackParticipant = fallbackParticipantQuery.data?.eventParticipantById;
    const fallbackDetail = fallbackParticipant?.id === target.participantId
        && fallbackParticipant.eventId === target.eventId
        ? {
            eventId: target.eventId,
            eventCode: "",
            eventName: "",
            participantId: fallbackParticipant.id,
            firstName: fallbackParticipant.firstName ?? "",
            lastName: fallbackParticipant.lastName ?? "",
            fullName: buildParticipantFullName(
                fallbackParticipant.firstName,
                fallbackParticipant.lastName,
                target.fallbackName
            ),
            company: fallbackParticipant.company ?? "",
            phoneNumber: fallbackParticipant.phoneNumber ?? "",
            email: fallbackParticipant.email ?? "",
            linkedInUrl: fallbackParticipant.linkedInUrl ?? "",
            additionalInfo: fallbackParticipant.additionalInfo ?? "",
            registrations: [],
            roomReservations: [],
            registrationCodes: [],
            certificates: []
        }
        : undefined;
    const activeDetail = detail?.eventId === target.eventId && detail.participantId === target.participantId
        ? detail
        : fallbackDetail;
    const isUsingFallbackDetail = !detail && !!fallbackDetail;
    const hasActiveDetail = !!activeDetail;

    const registrations = useMemo(
        () => presentItems<ParticipantRegistration>(activeDetail?.registrations),
        [activeDetail?.registrations]
    );
    const roomReservations = useMemo(
        () => presentItems<ParticipantRoomReservation>(activeDetail?.roomReservations),
        [activeDetail?.roomReservations]
    );
    const registrationCodes = useMemo(
        () => presentItems<ParticipantRegistrationCode>(activeDetail?.registrationCodes),
        [activeDetail?.registrationCodes]
    );
    const certificates = useMemo(
        () => presentItems<ParticipantCertificate>(activeDetail?.certificates),
        [activeDetail?.certificates]
    );
    const roomAllotmentOptions = useMemo(
        () => presentItems<EventParticipantRoomAllotmentOption>(roomAllotmentsQuery.data?.eventRoomAllotments?.items),
        [roomAllotmentsQuery.data?.eventRoomAllotments?.items]
    );
    const activityOptions = useMemo(
        () => presentItems<EventActivityOption>(activityOptionsQuery.data?.eventOptions?.items),
        [activityOptionsQuery.data?.eventOptions?.items]
    );
    const roomReservationOptions = useMemo<RoomReservationOption[]>(() => {
        const hotelNamesById = new Map(hotelOptions.map(hotel => [hotel.id, hotel.name?.trim() || `#${hotel.id}`]));
        const roomTypesById = new Map(roomTypeOptions.map(roomType => [roomType.id, roomType]));

        return roomAllotmentOptions
            .map(allotment => {
                const hotelName = hotelNamesById.get(allotment.hotelId)
                    ?? t("hotel-unavailable", {defaultValue: "Hotel unavailable"});
                const roomTypeName = roomTypesById.get(allotment.roomTypeId)?.name?.trim()
                    || t("room-type-unavailable", {defaultValue: "Room type unavailable"});
                const dateRange = [
                    formatLocalDate(allotment.startDate, EMPTY_OPERATIONS_VALUE),
                    formatLocalDate(allotment.endDate, EMPTY_OPERATIONS_VALUE)
                ]
                    .filter(value => value !== EMPTY_OPERATIONS_VALUE)
                    .join(" - ");

                return {
                    key: String(allotment.id),
                    allotmentId: allotment.id,
                    hotelId: allotment.hotelId,
                    roomTypeId: allotment.roomTypeId,
                    label: dateRange
                        ? `${hotelName} / ${roomTypeName} (${dateRange})`
                        : `${hotelName} / ${roomTypeName}`,
                    startDate: allotment.startDate,
                    endDate: allotment.endDate,
                    totalRooms: allotment.totalRooms
                };
            })
            .sort((left, right) => left.label.localeCompare(right.label, undefined, {sensitivity: "base"}));
    }, [hotelOptions, roomAllotmentOptions, roomTypeOptions, t]);

    const primaryRegistration = useMemo(() => pickPrimaryRegistration(registrations), [registrations]);
    const payerOptions = useMemo(() => {
        const map = new Map<number, ParticipantPayerOption>();

        presentItems<ParticipantPayerOption>(payerOptionsQuery.data?.payers?.items)
            .forEach(payer => map.set(payer.id, payer));

        if (primaryRegistration?.payerId && !map.has(primaryRegistration.payerId)) {
            map.set(primaryRegistration.payerId, {
                id: primaryRegistration.payerId,
                type: primaryRegistration.payerType,
                name: primaryRegistration.payerName,
                email: primaryRegistration.payerEmail,
                phone: primaryRegistration.payerPhone,
                address: primaryRegistration.payerAddress,
                city: primaryRegistration.payerCity,
                country: primaryRegistration.payerCountry,
                taxId: primaryRegistration.payerTaxId,
                contactPerson: primaryRegistration.payerContactPerson
            });
        }

        return Array.from(map.values()).sort((left, right) =>
            (left.name?.trim() || `#${left.id}`).localeCompare(right.name?.trim() || `#${right.id}`, undefined, {
                sensitivity: "base"
            }));
    }, [payerOptionsQuery.data?.payers?.items, primaryRegistration]);

    const watchedFirstName = Form.useWatch("firstName", participantForm);
    const watchedLastName = Form.useWatch("lastName", participantForm);
    const payerValues: Partial<PayerFormValues> = Form.useWatch(values => values, {form: payerForm, preserve: true}) ?? {};
    const selectedPayerId = payerValues.payerId;
    const selectedPayer = useMemo(
        () => payerOptions.find(payer => payer.id === selectedPayerId),
        [payerOptions, selectedPayerId]
    );
    const isActivityEditingLocked = !!primaryRegistration?.currentParticipationOfferId;
    const participantFullName = buildParticipantFullName(
        watchedFirstName ?? activeDetail?.firstName,
        watchedLastName ?? activeDetail?.lastName,
        activeDetail?.fullName || target.fallbackName
    );
    const latestCertificate = useMemo(
        () => [...certificates].sort((left, right) => right.id - left.id)[0],
        [certificates]
    );
    const primaryRoomReservation = useMemo(() => [...roomReservations]
        .filter(reservation => !primaryRegistration || reservation.registrationId === primaryRegistration.id)
        .sort((left, right) => right.id - left.id)[0], [primaryRegistration, roomReservations]);

    const refreshParticipantState = useCallback(async () => {
        await Promise.allSettled([refetchDetail(), fallbackParticipantQuery.refetch()]);
    }, [fallbackParticipantQuery, refetchDetail]);

    const getQrInvitationState = useCallback((registration: ParticipantRegistration): QrInvitationState => {
        const activeCodes = registrationCodes
            .filter(code => code.registrationId === registration.id && code.isActive);
        const revokedCodeCount = registrationCodes
            .filter(code => code.registrationId === registration.id && !code.isActive)
            .length;
        const eligibleStatus = isQrInvitable(registration.status);
        const hasParticipantEmail = !!activeDetail?.email?.trim();
        const hasValidParticipantEmail = isLikelyValidEmail(activeDetail?.email);
        const hasEventIdentifier = !!activeDetail?.eventId;
        const invitationEmailStatus = normalizeEnumKey(registration.invitationEmailStatus);
        const isQueued = invitationEmailStatus === "QUEUED";
        const isEmailConfirmed = registration.isEmailVerified;
        const canSend = eligibleStatus && isEmailConfirmed && hasValidParticipantEmail && hasEventIdentifier && !isQueued;

        let helperText = t("qr-ready-to-send", {defaultValue: "Ready to send"});

        if (!eligibleStatus) {
            helperText = t("qr-ineligible-status-reason", {
                defaultValue: "Available only for applied, rejected or invited registrations"
            });
        } else if (!isEmailConfirmed) {
            helperText = t("qr-email-not-confirmed", {
                defaultValue: "The participant has not confirmed the email address yet"
            });
        } else if (!hasParticipantEmail) {
            helperText = t("qr-missing-email", {
                defaultValue: "Add a participant email before sending QR"
            });
        } else if (!hasValidParticipantEmail) {
            helperText = t("qr-invalid-email", {
                defaultValue: "Enter a valid participant email before sending QR"
            });
        } else if (!hasEventIdentifier) {
            helperText = t("qr-event-setup-incomplete", {
                defaultValue: "Complete event setup before sending QR"
            });
        } else if (isQueued) {
            helperText = t("qr-email-queued", {
                defaultValue: "QR email is waiting for delivery."
            });
        } else if (invitationEmailStatus === "FAILED") {
            helperText = registration.invitationEmailFailureReason?.trim()
                || t("qr-invitation-send-failed", {defaultValue: "QR invitation could not be sent"});
        } else if (activeCodes.length > 0) {
            helperText = t("qr-ready-to-resend", {
                defaultValue: "Active QR can be sent again"
            });
        }

        return {
            activeCodeId: activeCodes[0]?.id,
            activeCodeCount: activeCodes.length,
            revokedCodeCount,
            canSend,
            helperText,
            actionLabel: activeCodes.length > 0
                ? t("resend-qr", {defaultValue: "Send QR again"})
                : t("send-qr", {defaultValue: "Send QR"}),
            showActiveTag: activeCodes.length > 0,
            showEligibleTag: eligibleStatus && isEmailConfirmed,
            emailStatus: registration.invitationEmailStatus
        };
    }, [activeDetail?.email, activeDetail?.eventId, registrationCodes, t]);
    const primaryQrInvitationState = useMemo(
        () => primaryRegistration ? getQrInvitationState(primaryRegistration) : undefined,
        [getQrInvitationState, primaryRegistration]
    );
    const primaryQrCodeId = primaryQrInvitationState?.activeCodeId
        ?? registrationCodes.find(code => code.registrationId === primaryRegistration?.id)?.id;

    useEffect(() => {
        setQrModalCodeId(undefined);
    }, [target.eventId, target.participantId]);

    useEffect(() => {
        if (!activeDetail) {
            return;
        }

        participantForm.setFieldsValue({
            firstName: activeDetail.firstName ?? "",
            lastName: activeDetail.lastName ?? "",
            company: activeDetail.company ?? "",
            phoneNumber: activeDetail.phoneNumber ?? "",
            email: activeDetail.email ?? "",
            linkedInUrl: activeDetail.linkedInUrl ?? "",
            additionalInfo: activeDetail.additionalInfo ?? ""
        });
    }, [
        activeDetail?.additionalInfo,
        activeDetail?.company,
        activeDetail?.email,
        activeDetail?.firstName,
        activeDetail?.lastName,
        activeDetail?.linkedInUrl,
        activeDetail?.phoneNumber,
        participantForm
    ]);

    useEffect(() => {
        if (!activeDetail) {
            return;
        }

        payerForm.setFieldsValue({
            payerId: primaryRegistration?.payerId ?? undefined,
            payerType: toPayerTypeInput(primaryRegistration?.payerType),
            payerName: primaryRegistration?.payerName?.trim() || buildParticipantFullName(
                activeDetail.firstName,
                activeDetail.lastName,
                target.fallbackName
            ),
            payerEmail: primaryRegistration?.payerEmail?.trim() || activeDetail.email?.trim() || "",
            payerPhone: primaryRegistration?.payerPhone?.trim() || activeDetail.phoneNumber?.trim() || "",
            payerAddress: primaryRegistration?.payerAddress?.trim() || "",
            payerCity: primaryRegistration?.payerCity?.trim() || "",
            payerCountry: primaryRegistration?.payerCountry?.trim() || "",
            payerTaxId: primaryRegistration?.payerTaxId?.trim() || "",
            payerContactPerson: primaryRegistration?.payerContactPerson?.trim() || ""
        });
    }, [
        activeDetail?.email,
        activeDetail?.firstName,
        activeDetail?.lastName,
        activeDetail?.phoneNumber,
        hasActiveDetail,
        payerForm,
        primaryRegistration?.payerId,
        primaryRegistration?.payerAddress,
        primaryRegistration?.payerCity,
        primaryRegistration?.payerContactPerson,
        primaryRegistration?.payerCountry,
        primaryRegistration?.payerEmail,
        primaryRegistration?.payerName,
        primaryRegistration?.payerPhone,
        primaryRegistration?.payerTaxId,
        primaryRegistration?.payerType,
        target.fallbackName
    ]);

    useEffect(() => {
        if (!activeDetail) {
            return;
        }

        const hydrationKey = [
            activeDetail.eventId,
            activeDetail.participantId,
            primaryRegistration?.id ?? 0,
            primaryRoomReservation?.id ?? 0
        ].join(":");
        const isNewHydrationSource = roomReservationHydrationKeyRef.current !== hydrationKey;

        roomReservationHydrationKeyRef.current = hydrationKey;

        // Keep operator edits while options load, unless the stored reservation changed.
        if (!isNewHydrationSource && !primaryRoomReservation) {
            return;
        }

        const matchingOption = primaryRoomReservation
            ? roomReservationOptions.find(option =>
                option.hotelId === primaryRoomReservation.hotelId
                && option.roomTypeId === primaryRoomReservation.roomTypeId)
            : undefined;

        roomReservationForm.setFieldsValue({
            roomOptionKey: matchingOption?.key,
            arrivalDate: primaryRoomReservation?.arrivalDate ?? matchingOption?.startDate ?? "",
            departureDate: primaryRoomReservation?.departureDate ?? matchingOption?.endDate ?? "",
            roomCount: Math.max(1, primaryRoomReservation?.roomCount ?? 1)
        });
    }, [
        activeDetail?.eventId,
        activeDetail?.participantId,
        primaryRegistration?.id,
        primaryRoomReservation?.arrivalDate,
        primaryRoomReservation?.departureDate,
        primaryRoomReservation?.hotelId,
        primaryRoomReservation?.id,
        primaryRoomReservation?.roomCount,
        primaryRoomReservation?.roomTypeId,
        roomReservationForm,
        roomReservationOptions
    ]);

    const activityFormValues = useMemo(
        () => buildActivityFormValues(primaryRegistration),
        // Rebuild only when the stored selections change, not on every refetch.
        [primaryRegistration?.id, primaryRegistration?.selectedEventOptionIds, primaryRegistration?.activityAnswers]
    );
    const activityFormSignature = JSON.stringify(activityFormValues);

    const hasActivityOptions = activityOptions.length > 0;

    useEffect(() => {
        // The activity form only renders once the event has activity options.
        if (!hasActiveDetail || !hasActivityOptions) {
            return;
        }

        // Reset first: setFieldsValue merges nested objects and would keep stale answers.
        activityForm.resetFields();
        activityForm.setFieldsValue(activityFormValues);
    }, [activityForm, activityFormSignature, hasActiveDetail, hasActivityOptions]);

    useEffect(() => {
        if (rejectingRegistration) {
            rejectForm.setFieldsValue({reason: rejectingRegistration.rejectionReason?.trim() ?? ""});
        }
    }, [rejectForm, rejectingRegistration]);

    const handleCertificateDownload = useCallback(async (certificate: ParticipantCertificate) => {
        setDownloadingCertificateId(certificate.id);

        try {
            const response = await fetch(buildParticipantOperationsFileDownloadUrl(certificate.fileId), {
                credentials: "include",
                headers: authorizationHeaders(auth.token)
            });

            if (!response.ok) {
                throw new Error("Download failed");
            }

            await downloadResponseFile(response, certificate.fileName?.trim() || "certificate.pdf");
        } catch {
            message.error(t("error.download-failed", {defaultValue: "Download failed"}));
        } finally {
            setDownloadingCertificateId(undefined);
        }
    }, [message, auth.token, t]);

    const handleQrInvitationSend = useCallback(async (registration: ParticipantRegistration) => {
        setInvitingQrRegistrationId(registration.id);

        try {
            await inviteRegistration({
                variables: {
                    input: {
                        id: registration.id
                    }
                }
            });
            await refreshParticipantState();
            message.success(t("qr-invitation-sent", {defaultValue: "QR invitation queued"}));
        } catch {
            message.error(t("qr-invitation-send-failed", {
                defaultValue: "QR invitation could not be sent"
            }));
        } finally {
            setInvitingQrRegistrationId(undefined);
        }
    }, [message, inviteRegistration, refreshParticipantState, t]);

    const handleVerificationEmailResend = useCallback(async (registration: ParticipantRegistration) => {
        setResendingVerificationRegistrationId(registration.id);

        try {
            await resendRegistrationVerification({
                variables: {
                    input: {
                        id: registration.id
                    }
                }
            });
            await refreshParticipantState();
            message.success(t("verification-email-queued", {defaultValue: "Verification email queued."}));
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("verification-email-queue-failed", {
                    defaultValue: "Verification email could not be queued."
                })
            }));
        } finally {
            setResendingVerificationRegistrationId(undefined);
        }
    }, [message, errorLabels, refreshParticipantState, resendRegistrationVerification, t]);

    const handleCertificateGenerate = useCallback(async (registration: ParticipantRegistration) => {
        setGeneratingCertificateRegistrationId(registration.id);

        try {
            await generateCertificate({
                variables: {
                    input: {
                        registrationId: registration.id
                    }
                }
            });
            message.success(t("certificate-generated", {
                defaultValue: "Certificate ready"
            }));
            await refreshParticipantState();
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("certificate-generation-failed", {defaultValue: "Certificate could not be generated"})
            }));
        } finally {
            setGeneratingCertificateRegistrationId(undefined);
        }
    }, [message, errorLabels, generateCertificate, refreshParticipantState, t]);

    const handleCertificateEmailResend = useCallback(async (certificate: ParticipantCertificate) => {
        setResendingCertificateId(certificate.id);

        try {
            await resendCertificateEmail({
                variables: {
                    input: {
                        eventId: certificate.eventId,
                        certificateId: certificate.id
                    }
                }
            });
            await refreshParticipantState();
            message.success(t("certificate-email-queued", {defaultValue: "Certificate email queued."}));
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("certificate-email-queue-failed", {
                    defaultValue: "Certificate email could not be queued."
                })
            }));
        } finally {
            setResendingCertificateId(undefined);
        }
    }, [message, errorLabels, refreshParticipantState, resendCertificateEmail, t]);

    const openRegistrationReject = useCallback((registration: ParticipantRegistration) => {
        if (!isRegistrationRejectable(registration.status)) {
            return;
        }

        setRejectingRegistration(registration);
    }, []);

    const closeRegistrationReject = useCallback(() => setRejectingRegistration(undefined), []);

    const handleRegistrationReject = useCallback(async ({reason}: {reason: string}) => {
        const registration = rejectingRegistration;

        if (!activeDetail || !registration) {
            return;
        }

        setSavingRegistration(true);

        try {
            await rejectEventRegistration({
                variables: {
                    input: {
                        id: registration.id,
                        eventId: activeDetail.eventId,
                        reason: reason.trim()
                    }
                }
            });
            setRejectingRegistration(undefined);
            await refreshParticipantState();
            message.success(t("participant-rejection-success", {
                defaultValue: "Registration rejected."
            }));
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("participant-rejection-failed", {
                    defaultValue: "Registration could not be rejected."
                })
            }));
        } finally {
            setSavingRegistration(false);
        }
    }, [message, activeDetail, errorLabels, refreshParticipantState, rejectEventRegistration, rejectingRegistration, t]);

    const handleParticipantSave = useCallback(async () => {
        if (!activeDetail) {
            return;
        }

        // Read the whole store: the note has no field on the page but is saved back unchanged.
        const values = participantForm.getFieldsValue(true) as Partial<ParticipantFormValues>;
        setSavingParticipant(true);

        try {
            await updateEventParticipant({
                variables: {
                    input: {
                        id: activeDetail.participantId,
                        eventId: activeDetail.eventId,
                        firstName: values.firstName?.trim() ?? "",
                        lastName: values.lastName?.trim() ?? "",
                        company: values.company?.trim() ?? "",
                        phoneNumber: values.phoneNumber?.trim() ?? "",
                        email: values.email?.trim() ?? "",
                        linkedInUrl: values.linkedInUrl?.trim() ?? "",
                        additionalInfo: values.additionalInfo?.trim() ?? ""
                    }
                }
            });
            await refreshParticipantState();
            message.success(t("participant-save-success", {
                defaultValue: "Participant details saved."
            }));
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("participant-save-failed", {
                    defaultValue: "Participant details could not be saved."
                })
            }));
        } finally {
            setSavingParticipant(false);
        }
    }, [message, activeDetail, errorLabels, participantForm, refreshParticipantState, t, updateEventParticipant]);

    const handleParticipantDelete = useCallback(async () => {
        try {
            const result = await deleteEventParticipant({
                variables: {
                    input: {
                        id: target.participantId
                    }
                },
                update: cache => {
                    const participantCacheId = cache.identify({
                        __typename: "EventParticipantReturnDto",
                        id: target.participantId
                    });

                    if (participantCacheId) {
                        cache.evict({id: participantCacheId});
                        cache.gc();
                    }
                }
            });

            if (!result.data?.deleteEventParticipant) {
                throw new Error(t("participant-delete-failed", {
                    defaultValue: "Participant could not be deleted."
                }));
            }

            message.success(t("participant-delete-success", {
                defaultValue: "Participant deleted."
            }));
            onDeleted?.();
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("participant-delete-failed", {
                    defaultValue: "Participant could not be deleted."
                })
            }));
        }
    }, [message, deleteEventParticipant, errorLabels, onDeleted, t, target.participantId]);

    const handlePayerSelect = useCallback((value: unknown) => {
        const payer = payerOptions.find(candidate => candidate.id === value);

        if (payer) {
            payerForm.setFieldsValue(payerFormValuesFromPayer(payer));
        }
    }, [payerForm, payerOptions]);

    // Payer change is its own action with its own feedback; the server refuses it once offers were issued.
    const handlePayerChange = useCallback(async () => {
        const payerId = payerForm.getFieldValue("payerId") as number | undefined;
        if (!activeDetail || !primaryRegistration) {
            return;
        }

        if (!payerId) {
            message.warning(t("participant-payer-required", {defaultValue: "Select a payer first."}));
            return;
        }

        setChangingPayer(true);
        try {
            await changeEventRegistrationPayer({
                variables: {
                    input: {
                        id: primaryRegistration.id,
                        eventId: activeDetail.eventId,
                        payerId
                    }
                }
            });
            await refreshParticipantState();
            message.success(t("participant-payer-change-success", {defaultValue: "Payer changed."}));
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("participant-payer-change-failed", {defaultValue: "The payer could not be changed."})
            }));
        } finally {
            setChangingPayer(false);
        }
    }, [message, activeDetail, changeEventRegistrationPayer, errorLabels, payerForm, primaryRegistration, refreshParticipantState, t]);

    const handleRoomReservationOptionChange = useCallback((value: unknown) => {
        const selectedOption = roomReservationOptions.find(option => option.key === value);

        if (selectedOption) {
            roomReservationForm.setFieldsValue({
                arrivalDate: selectedOption.startDate ?? roomReservationForm.getFieldValue("arrivalDate"),
                departureDate: selectedOption.endDate ?? roomReservationForm.getFieldValue("departureDate")
            });
        }
    }, [roomReservationForm, roomReservationOptions]);

    const handleRoomReservationSave = useCallback(async (values: RoomReservationFormValues) => {
        const selectedOption = roomReservationOptions.find(option => option.key === values.roomOptionKey);
        const roomCount = toPositiveInt(values.roomCount);

        if (!primaryRegistration || !activeDetail || !selectedOption || !roomCount) {
            return;
        }

        setSavingRoomReservation(true);

        try {
            await saveEventRoomReservation({
                variables: {
                    input: {
                        id: primaryRoomReservation?.id ?? 0,
                        eventId: activeDetail.eventId,
                        registrationId: primaryRegistration.id,
                        participantId: activeDetail.participantId,
                        hotelId: selectedOption.hotelId,
                        roomTypeId: selectedOption.roomTypeId,
                        arrivalDate: values.arrivalDate,
                        departureDate: values.departureDate,
                        roomCount
                    }
                }
            });
            await refreshParticipantState();
            message.success(t("participant-room-reservation-save-success", {
                defaultValue: "Accommodation reservation saved."
            }));
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("participant-room-reservation-save-failed", {
                    defaultValue: "Accommodation reservation could not be saved."
                })
            }));
        } finally {
            setSavingRoomReservation(false);
        }
    }, [
        message,
        activeDetail,
        errorLabels,
        primaryRegistration,
        primaryRoomReservation?.id,
        refreshParticipantState,
        roomReservationOptions,
        saveEventRoomReservation,
        t
    ]);

    const handleActivitiesSave = useCallback(async (values: ActivityFormValues) => {
        if (!primaryRegistration || !activeDetail) {
            return;
        }

        setSavingActivities(true);

        try {
            const yesNoActivityOptionIds = new Set(activityOptions
                .filter(option => normalizeEventActivityQuestionType(option.questionType) === "YesNo")
                .map(option => option.id));
            const {visibleOptions, selectedOptionIds: selectedYesNoOptionIds} = resolveVisibleActivitySelection(
                activityOptions,
                primaryRegistration.selectedParticipationFeeTypeId,
                toPositiveIds(values.yesNoOptionIds).filter(optionId => yesNoActivityOptionIds.has(optionId)));
            const answerValues = Object.fromEntries(Object.entries(values.answers ?? {})
                .map(([optionId, value]) => [Number(optionId), value]));

            await updateEventRegistrationActivities({
                variables: {
                    input: {
                        id: primaryRegistration.id,
                        eventId: activeDetail.eventId,
                        selectedOptionIds: selectedYesNoOptionIds,
                        activityAnswers: buildActivityAnswerInputs(visibleOptions, selectedYesNoOptionIds, answerValues)
                    }
                }
            });
            await refreshParticipantState();
            message.success(t("participant-activities-save-success", {
                defaultValue: "Participant activities saved."
            }));
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, {
                ...errorLabels,
                fallback: t("participant-activities-save-failed", {
                    defaultValue: "Participant activities could not be saved."
                })
            }));
        } finally {
            setSavingActivities(false);
        }
    }, [
        message,
        activeDetail,
        activityOptions,
        errorLabels,
        primaryRegistration,
        refreshParticipantState,
        t,
        updateEventRegistrationActivities
    ]);

    return {
        auth,
        target,
        detailQuery,
        activeDetail,
        isUsingFallbackDetail,
        registrations,
        roomReservations,
        registrationCodes,
        certificates,
        primaryRegistration,
        primaryRoomReservation,
        latestCertificate,
        participantFullName,
        canGeneratePrimaryCertificate: !!primaryRegistration
            && canGenerateCertificateForRegistration(primaryRegistration.status),
        forms: {
            participant: participantForm,
            payer: payerForm,
            roomReservation: roomReservationForm,
            activity: activityForm,
            reject: rejectForm
        },
        payer: {
            options: payerOptions,
            loading: payerOptionsQuery.loading,
            values: payerValues,
            selected: selectedPayer,
            setSearch: setPayerSearch,
            select: handlePayerSelect,
            // Offers carry the payer's details, so the payer is fixed once a participation offer exists.
            isLocked: !!primaryRegistration?.currentParticipationOfferId,
            isChanged: !!selectedPayerId && selectedPayerId !== primaryRegistration?.payerId
        },
        registrationModal: {
            open: registrationModalOpen,
            show: () => setRegistrationModalOpen(true),
            close: () => setRegistrationModalOpen(false),
            onRegistered: async () => {
                setRegistrationModalOpen(false);
                await refreshParticipantState();
            }
        },
        rooming: {
            options: roomReservationOptions,
            loading: accommodationOptionsLoading,
            failed: accommodationOptionsFailed,
            onOptionChange: handleRoomReservationOptionChange
        },
        activities: {
            options: activityOptions,
            isLocked: isActivityEditingLocked
        },
        qr: {
            getState: getQrInvitationState,
            primaryState: primaryQrInvitationState,
            primaryCodeId: primaryQrCodeId,
            modalCodeId: qrModalCodeId,
            openModal: setQrModalCodeId,
            closeModal: () => setQrModalCodeId(undefined)
        },
        rejectingRegistration,
        pending: {
            downloadingCertificateId,
            generatingCertificateRegistrationId,
            resendingCertificateId,
            invitingQrRegistrationId,
            resendingVerificationRegistrationId,
            changingPayer,
            savingParticipant,
            savingRegistration,
            savingActivities,
            savingRoomReservation,
            deletingParticipant: deleteParticipantState.loading
        },
        actions: {
            downloadCertificate: handleCertificateDownload,
            sendQrInvitation: handleQrInvitationSend,
            resendVerificationEmail: handleVerificationEmailResend,
            generateCertificate: handleCertificateGenerate,
            resendCertificateEmail: handleCertificateEmailResend,
            openRegistrationReject,
            closeRegistrationReject,
            rejectRegistration: handleRegistrationReject,
            saveParticipant: handleParticipantSave,
            deleteParticipant: handleParticipantDelete,
            changePayer: handlePayerChange,
            saveRoomReservation: handleRoomReservationSave,
            saveActivities: handleActivitiesSave
        }
    };
};

export type ParticipantDetailModel = ReturnType<typeof useParticipantDetail>;
